import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  TextField,
  Tooltip,
  Button,
  makeStyles,
  FormHelperText,
} from "@material-ui/core";
import Box from "@mui/material/Box";
import styled from "styled-components";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import draftToHtml from "draftjs-to-html";
import ReactHtmlParser from "react-html-parser";
import TextEditor from "./TextEditor.js";

import AlertDialog from "../../../components/AlertDialog.js";
import infoImg from "../../../assets/icons/info.png";

const StyledTooltip = styled(props => (
  <Tooltip
    classes={{ popper: props.className }}
    {...props}
    PopperProps={{
      popperOptions: {
        modifiers: {
          flip: { enabled: false },
          offset: {
            offset: "0px, -35px, 20px, -25px",
          },
        },
      },
    }}
  />
))`
  & .MuiTooltip-tooltip {
    display: flex;
    background: #757575;
    color: #fff;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
  }
`;
const useStyles = makeStyles(theme => ({
  outlinedButtonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#0D47A1",
    height: "34px",
    textTransform: "none",
  },
  buttonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#fff",
    height: "34px",
    backgroundColor: "#0D47A1",
    textTransform: "none",
    "&:active": {
      backgroundColor: "#0D47A1",
    },
    "&:hover": {
      backgroundColor: "#1E88E5",
    },
  },
  subDialog: {
    "& .MuiDialogContent-root": {
      padding: 0,
    },
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: "1100px",
      height: "600px",
    },
  },
  textFieldStyle: {
    marginBottom: 5,
    marginTop: 20,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 4,
      fontSize: 16,
      backgroundColor: "#fff",
    },
  },
  textFieldOnFormStyle: {
    marginBottom: 5,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 16,
      backgroundColor: "#fff",
    },
  },

  switch: {
    "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
      backgroundColor: "#0D47A1",
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#0D47A1",
    },
  },
  errorStyle: {
    marginTop: 0,
    fontSize: 14, //16,
    fontFamily: "Arial",
  },
}));

export default function EditContentToFormDialog({
  closeDialog,
  handleDone,
  queText,
  queRaw,
  capText,
  capRaw,
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [queTextEdit, setQueTextEdit] = useState(queText);
  const [queRawEdit, setQueRawEdit] = useState(queRaw);
  const [capTextEdit, setCapTextEdit] = useState(capText);
  const [capRawEdit, setCapRawEdit] = useState(capRaw);
  const [alertMsg, setAlertMsg] = useState([]);

  const editorRef = useRef(null);
  const [checkError, setCheckError] = useState(false);

  const handleClose = () => {
    setOpen(false);
    closeDialog && closeDialog();
  };

  const handleChangeTextEditor = (txt, type) => {
    let contentHTML = draftToHtml(txt);
    contentHTML = contentHTML.replace(/<p>/, "");

    contentHTML = contentHTML.replace(
      /<a[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi,
      (match, href, text) => {
        // Remove leading protocols
        href = href.replace(/^(https?:\/\/)+/, "");

        // Prepend https://
        href = `https://${href}`;

        return `<a href="${href}" target="_blank">${text}</a>`;
      }
    );

    const convertedTxt = ReactHtmlParser(contentHTML);
    
    if (type === "question") {
      setQueTextEdit(convertedTxt);
      setQueRawEdit(txt);
    }
    if (type === "caption") {
      setCapTextEdit(convertedTxt);
      setCapRawEdit(txt);
    }
  };

  const validateFields = () => {
    setCheckError(true);
    if (queRawEdit.blocks[0].text.trim() === "") {
      editorRef.current.focus();
      return false;
    }
    return true;
  };

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="xl"
      //  onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={classes.subDialog}
    >
      <DialogTitle sx={{ p: 0 }} id="alert-dialog-title">
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Grid container>
            <Grid
              item
              lg={4}
              xl={4}
              md={4}
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                paddingBottom: 15,
              }}
            >
              <Box style={{ width: 100 }}>
                <IconButton
                  aria-label="close"
                  sx={{
                    position: "absolute",
                    left: 8,
                    top: 8,
                    align: "right",
                    color: theme => theme.palette.grey[500],
                  }}
                  onClick={e => {
                    if (
                      queRawEdit.blocks[0].text !== queRaw.blocks[0].text ||
                      capRawEdit.blocks[0].text !== capRaw.blocks[0].text
                    )
                      setAlertMsg({
                        title: "Exit",
                        type: "saveConfirmation",
                        msg: "Are you sure you want to exit without saving your work?",
                      });
                    else handleClose();
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box pt={2} style={{ width: 350 }}>
                <Typography
                  variant="h1"
                  style={{
                    fontSize: 20,
                    display: "flex",
                    //    flexDirection: "end",
                    alignItems: "center",
                  }}
                >
                  Object: Content
                </Typography>
              </Box>
            </Grid>
            <Grid item lg={8} xl={8} md={8} style={{ backgroundColor: "#E3F2FD" }}></Grid>
          </Grid>
        </Box>
      </DialogTitle>
      <DialogContent
        style={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Grid container>
          <Grid item lg={4} xl={4} md={4}>
            <Card style={{ maxHeight: "550px", overflowY: "scroll" }}>
              <Box mb={1} mt={5}>
                <Divider />
                <Box mt={2} ml={2}>
                  <Typography variant="h3">Content Text*</Typography>
                </Box>
                <Box mt={2} ml={2} mb={2} mr={2}>
                  <TextEditor
                    type="question" // Content Text
                    value={queRaw}
                    name="content"
                    maxLength={600}
                    handleChangeText={(txt, type) => handleChangeTextEditor(txt, type)}
                    editorRef={editorRef}
                  />
                  {checkError &&
                    queRawEdit &&
                    queRawEdit.blocks[0].text.trim() === "" && (
                      <FormHelperText className={classes.errorStyle} error>
                        Question field cannot be blank.
                      </FormHelperText>
                    )}
                </Box>
                <Divider />
                <Box mt={2} ml={2}>
                  <Typography variant="h3">Caption</Typography>
                </Box>
                <Box mt={2} ml={2} mb={2} mr={2}>
                  <TextEditor
                    type="caption"
                    value={capRaw}
                    maxLength={600}
                    handleChangeText={(txt, type) => handleChangeTextEditor(txt, type)}
                  />
                </Box>
                <Box
                  mt={5}
                  ml={2}
                  //  mb={3}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    // background: "white",
                    // position: "sticky",
                    // top: 20,
                    // bottom: 20,
                    // paddingTop: "40px",
                    // paddingBottom: "40px",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.buttonStyle}
                    style={{ marginRight: 2 }}
                    onClick={() => {
                      if (!validateFields()) return;
                      handleDone(
                        "Content",
                        "done",
                        null,
                        null,
                        queTextEdit,
                        queRawEdit,
                        capTextEdit,
                        capRawEdit,
                        null,
                        null,
                        null,
                        null,
                        null,
                        null,
                        null,
                        true
                      );
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="text"
                    color="primary"
                    className={classes.outlinedButtonStyle}
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item lg={8} md={8} xl={8}>
            <Card style={{ backgroundColor: "#E3F2FD" }}>
              <Box
                mt={6}
                mb={1.5}
                ml={10}
                mr={10}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Typography
                    variant="h3"
                    style={{
                      display: "flex",
                    }}
                  >
                    <div
                      style={{
                        wordBreak: "break-word",
                      }}
                    >
                      {queTextEdit ? queTextEdit : ""}
                    </div>
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  style={{
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    paddingBottom: 5,
                    paddingTop: 20,
                  }}
                >
                  <div
                    style={{
                      wordBreak: "break-word",
                    }}
                  >
                    {capTextEdit ? capTextEdit : ""}
                  </div>
                </Typography>
              </Box>
            </Card>
            {alertMsg.msg && (
              <AlertDialog
                title={alertMsg.title}
                type={alertMsg.type}
                contentMsg={alertMsg.msg}
                closeDialog={() => setAlertMsg([])}
                cancelSaveAction={() => {
                  setAlertMsg([]);
                  handleClose();
                }}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
